// Event selector dropdown for switching between multiple events

import React, { useRef, useEffect } from 'react'
import { SupplierEvent } from '../../hooks/useSupplierTasks'
import styles from './EventSelector.module.css'

interface EventSelectorProps {
  events: SupplierEvent[]
  selectedEventId: string
  onEventChange: (eventId: string) => void
  onClose: () => void
}

export const EventSelector: React.FC<EventSelectorProps> = ({
  events,
  selectedEventId,
  onEventChange,
  onClose
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const handleEventSelect = (eventId: string) => {
    if (eventId !== selectedEventId) {
      onEventChange(eventId)
    }
    onClose()
  }

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow'
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      })
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return '🟢'
      case 'draft':
        return '🟡'
      case 'completed':
        return '✅'
      case 'cancelled':
        return '🔴'
      default:
        return '📅'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active'
      case 'draft':
        return 'Draft'
      case 'completed':
        return 'Completed'
      case 'cancelled':
        return 'Cancelled'
      default:
        return status
    }
  }

  if (events.length <= 1) {
    return null
  }

  return (
    <div className={styles.overlay}>
      <div 
        ref={dropdownRef}
        className={styles.dropdown}
        role="listbox"
        aria-label="Select event"
      >
        <div className={styles.header}>
          <h3 className={styles.title}>Select Event</h3>
          <p className={styles.subtitle}>Switch between your assigned events</p>
        </div>

        <div className={styles.eventList}>
          {events.map((event) => {
            const isSelected = event.id === selectedEventId
            const isActive = event.status === 'active'

            return (
              <button
                key={event.id}
                className={`
                  ${styles.eventItem} 
                  ${isSelected ? styles.selected : ''}
                  ${isActive ? styles.active : ''}
                `.trim()}
                onClick={() => handleEventSelect(event.id)}
                role="option"
                aria-selected={isSelected}
              >
                <div className={styles.eventHeader}>
                  <div className={styles.eventTitle}>
                    {event.name}
                    {isSelected && (
                      <span className={styles.currentLabel}>Current</span>
                    )}
                  </div>
                  <div className={styles.eventStatus}>
                    <span className={styles.statusIcon}>
                      {getStatusIcon(event.status)}
                    </span>
                    <span className={styles.statusText}>
                      {getStatusText(event.status)}
                    </span>
                  </div>
                </div>

                <div className={styles.eventMeta}>
                  <div className={styles.eventDate}>
                    <span className={styles.dateIcon}>📅</span>
                    {formatEventDate(event.date)}
                  </div>
                  {event.venue && (
                    <div className={styles.eventVenue}>
                      <span className={styles.venueIcon}>📍</span>
                      {event.venue}
                    </div>
                  )}
                </div>

                {isSelected && (
                  <div className={styles.selectedIndicator}>
                    <span className={styles.checkIcon}>✓</span>
                  </div>
                )}
              </button>
            )
          })}
        </div>

        <div className={styles.footer}>
          <button 
            className={styles.closeButton}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}